import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../axiosBaseQuery";
import type { Response } from "../interfaces";

export type CsmStatus = "open" | "waiting_on_them" | "waiting_on_us" | "on_hold" | "closed";
export type CsmPriority = "low" | "medium" | "high";
export type CsmContactType = "lead" | "customer" | "vendor" | "partner" | "other";
export type CsmActivityType = "note" | "call" | "email" | "meeting" | "sms" | "system";
export type CsmDueFilter = "overdue" | "today" | "week" | "upcoming" | "none";
export type CsmSort = "follow_up" | "updated" | "created" | "priority";
export type CsmOwner = "Najla" | "Akshay";

export interface CsmActivity {
  id: string;
  type: CsmActivityType;
  body: string;
  created_at: string;
  created_by: string | null;
}

interface CsmFollowupBase {
  csm_followup_id: string;
  contact_name: string;
  company: string | null;
  email: string | null;
  phone: string | null;
  subject: string;
  contact_type: CsmContactType;
  status: CsmStatus;
  priority: CsmPriority;
  owner: string | null;
  next_follow_up_date: string | null;
  next_step: string | null;
  notes: string | null;
  last_contacted_at: string | null;
  closed_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CsmFollowupListItem extends CsmFollowupBase {
  activity_count: number;
  last_activity: CsmActivity | null;
}

export interface CsmFollowup extends CsmFollowupBase {
  activity: CsmActivity[];
}

export interface CsmListResult {
  items: CsmFollowupListItem[];
  total: number;
  page: number;
  limit: number;
}

export interface CsmSummary {
  today: string;
  overdue: number;
  due_today: number;
  due_this_week: number;
  no_follow_up: number;
  active: number;
  waiting_on_us: number;
  closed_last_30_days: number;
}

export interface CsmListParams {
  search?: string;
  status?: CsmStatus | "active" | "all";
  priority?: CsmPriority;
  contact_type?: CsmContactType;
  due?: CsmDueFilter;
  owner?: CsmOwner;
  sort?: CsmSort;
  today?: string;
  page?: number;
  limit?: number;
}

export interface CsmFollowupInput {
  contact_name?: string;
  subject?: string;
  company?: string | null;
  email?: string | null;
  phone?: string | null;
  contact_type?: CsmContactType;
  status?: CsmStatus;
  priority?: CsmPriority;
  owner?: CsmOwner | null;
  next_follow_up_date?: string | null;
  next_step?: string | null;
  notes?: string | null;
}

export interface CsmActivityInput {
  type: Exclude<CsmActivityType, "system">;
  body: string;
  next_follow_up_date?: string | null;
  next_step?: string | null;
  status?: CsmStatus;
}

const dropEmpty = (params: CsmListParams) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== ""),
  );

export const csmApi = createApi({
  reducerPath: "csmApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["CsmList", "CsmDetail", "CsmSummary"],
  endpoints: (builder) => ({
    getCsmFollowups: builder.query<Response<CsmListResult>, CsmListParams>({
      query: (params) => ({ url: "admin/csm", method: "GET", params: dropEmpty(params) }),
      providesTags: ["CsmList"],
    }),
    getCsmSummary: builder.query<Response<CsmSummary>, string>({
      query: (today) => ({ url: "admin/csm/summary", method: "GET", params: { today } }),
      providesTags: ["CsmSummary"],
    }),
    getCsmFollowup: builder.query<Response<CsmFollowup>, string>({
      query: (id) => ({ url: `admin/csm/${id}`, method: "GET" }),
      providesTags: (_result, _error, id) => [{ type: "CsmDetail", id }],
    }),
    createCsmFollowup: builder.mutation<Response<CsmFollowup>, CsmFollowupInput>({
      query: (data) => ({ url: "admin/csm", method: "POST", data }),
      invalidatesTags: ["CsmList", "CsmSummary"],
    }),
    updateCsmFollowup: builder.mutation<
      Response<CsmFollowup>,
      { id: string; data: CsmFollowupInput }
    >({
      query: ({ id, data }) => ({ url: `admin/csm/${id}`, method: "PATCH", data }),
      invalidatesTags: (_result, _error, { id }) => [
        "CsmList",
        "CsmSummary",
        { type: "CsmDetail", id },
      ],
    }),
    addCsmActivity: builder.mutation<
      Response<CsmFollowup>,
      { id: string; data: CsmActivityInput }
    >({
      query: ({ id, data }) => ({ url: `admin/csm/${id}/activity`, method: "POST", data }),
      invalidatesTags: (_result, _error, { id }) => [
        "CsmList",
        "CsmSummary",
        { type: "CsmDetail", id },
      ],
    }),
    removeCsmActivity: builder.mutation<
      Response<CsmFollowup>,
      { id: string; activityId: string }
    >({
      query: ({ id, activityId }) => ({
        url: `admin/csm/${id}/activity/${activityId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { id }) => ["CsmList", { type: "CsmDetail", id }],
    }),
    deleteCsmFollowup: builder.mutation<Response<{ csm_followup_id: string }>, string>({
      query: (id) => ({ url: `admin/csm/${id}`, method: "DELETE" }),
      invalidatesTags: ["CsmList", "CsmSummary"],
    }),
  }),
});

export const {
  useGetCsmFollowupsQuery,
  useGetCsmSummaryQuery,
  useGetCsmFollowupQuery,
  useCreateCsmFollowupMutation,
  useUpdateCsmFollowupMutation,
  useAddCsmActivityMutation,
  useRemoveCsmActivityMutation,
  useDeleteCsmFollowupMutation,
} = csmApi;
